import { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Chip, Box, Skeleton, alpha } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { ClientSummary } from '../../types';
import { formatCurrency, formatPercentage } from '../../utils/formatters';

interface ClientSummaryTableProps {
  data: ClientSummary[];
  loading?: boolean;
  onRowClick?: (clientId: string) => void;
}

const COLORS = {
  positive: '#10B981',
  negative: '#EF4444',
  warning: '#F59E0B',
  text: '#0F172A',
  textMuted: '#64748B',
  border: '#E2E8F0',
  rowHover: 'rgba(16, 185, 129, 0.08)',
};

export function ClientSummaryTable({ data, loading = false, onRowClick }: ClientSummaryTableProps) {
  const navigate = useNavigate();

  const handleRowClick = useCallback(
    (clientId: string) => {
      if (onRowClick) {
        onRowClick(clientId);
      } else {
        navigate(`/clients/${clientId}`);
      }
    },
    [navigate, onRowClick]
  );

  // Column definitions
  const columnDefs = useMemo<ColDef<ClientSummary>[]>(
    () => [
      {
        headerName: 'Client',
        field: 'clientName',
        flex: 1.2,
        minWidth: 150,
        sortable: true,
        filter: true,
        cellRenderer: (params: ICellRendererParams<ClientSummary>) => (
          <Box display="flex" alignItems="center" height="100%">
            <Typography variant="body2" fontWeight={600} color={COLORS.text}>
              {params.value}
            </Typography>
          </Box>
        ),
      },
      {
        headerName: 'Invested',
        field: 'totalInvested',
        flex: 1,
        minWidth: 130,
        sortable: true,
        cellRenderer: (params: ICellRendererParams<ClientSummary>) => (
          <Box display="flex" alignItems="center" height="100%">
            <Typography variant="body2" color={COLORS.textMuted}>
              {formatCurrency(params.value)}
            </Typography>
          </Box>
        ),
      },
      {
        headerName: 'Current Value',
        field: 'currentValue',
        flex: 1,
        minWidth: 130,
        sortable: true,
        cellRenderer: (params: ICellRendererParams<ClientSummary>) => (
          <Box display="flex" alignItems="center" height="100%">
            <Typography variant="body2" color={COLORS.text} fontWeight={500}>
              {formatCurrency(params.value)}
            </Typography>
          </Box>
        ),
      },
      {
        headerName: 'P&L',
        field: 'totalPnL',
        flex: 1,
        minWidth: 120,
        sortable: true,
        cellRenderer: (params: ICellRendererParams<ClientSummary>) => {
          const value = params.value as number;
          const color = value >= 0 ? COLORS.positive : COLORS.negative;
          return (
            <Box display="flex" alignItems="center" height="100%">
              <Typography variant="body2" fontWeight={700} sx={{ color }}>
                {formatCurrency(value)}
              </Typography>
            </Box>
          );
        },
      },
      {
        headerName: 'Return',
        field: 'pnlPercentage',
        flex: 0.7,
        minWidth: 90,
        sortable: true,
        cellRenderer: (params: ICellRendererParams<ClientSummary>) => {
          const value = params.value as number;
          const color = value > 0 ? COLORS.positive : value < -10 ? COLORS.negative : COLORS.warning;
          return (
            <Box display="flex" alignItems="center" height="100%">
              <Chip
                label={formatPercentage(value)}
                size="small"
                sx={{
                  bgcolor: alpha(color, 0.12),
                  color: color,
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  height: 26,
                  borderRadius: '8px',
                }}
              />
            </Box>
          );
        },
      },
      {
        headerName: 'Brokerage',
        field: 'totalBrokerage',
        flex: 1,
        minWidth: 120,
        sortable: true,
        cellRenderer: (params: ICellRendererParams<ClientSummary>) => (
          <Box display="flex" alignItems="center" height="100%">
            <Typography variant="body2" fontWeight={500} color={COLORS.text}>
              {formatCurrency(params.value)}
            </Typography>
          </Box>
        ),
      },
      {
        headerName: 'Turnover',
        field: 'totalTurnover',
        flex: 1,
        minWidth: 120,
        sortable: true,
        cellRenderer: (params: ICellRendererParams<ClientSummary>) => (
          <Box display="flex" alignItems="center" height="100%">
            <Typography variant="body2" color={COLORS.textMuted}>
              {formatCurrency(params.value)}
            </Typography>
          </Box>
        ),
      },
    ],
    []
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
    }),
    []
  );

  if (loading) {
    return (
      <Box>
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} width="100%" height={48} sx={{ mb: 0.5, borderRadius: '8px' }} />
        ))}
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height={200}
        sx={{
          bgcolor: '#F8FAFC',
          borderRadius: '16px',
          border: '1px dashed #E2E8F0',
        }}
      >
        <Typography color="text.secondary" fontWeight={500}>
          No client data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      className="ag-theme-alpine"
      sx={{
        height: 420,
        width: '100%',
        '& .ag-root-wrapper': {
          borderRadius: '16px',
          border: `1px solid ${COLORS.border}`,
          overflow: 'hidden',
        },
        '& .ag-header': {
          bgcolor: '#F8FAFC',
          borderBottom: `1px solid ${COLORS.border}`,
        },
        '& .ag-header-cell': {
          fontSize: '0.75rem',
          fontWeight: 700,
          color: COLORS.textMuted,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        },
        '& .ag-header-cell-label': {
          fontWeight: 700,
        },
        '& .ag-row': {
          cursor: 'pointer',
          borderBottom: `1px solid ${COLORS.border}`,
          transition: 'all 0.15s ease',
        },
        '& .ag-row:hover': {
          backgroundColor: `${COLORS.rowHover} !important`,
        },
        '& .ag-row-even': {
          backgroundColor: '#FFFFFF',
        },
        '& .ag-row-odd': {
          backgroundColor: '#FAFBFC',
        },
        '& .ag-cell': {
          display: 'flex',
          alignItems: 'center',
          fontSize: '0.875rem',
          padding: '0 16px',
        },
        '& .ag-paging-panel': {
          borderTop: `1px solid ${COLORS.border}`,
        },
      }}
    >
      <AgGridReact<ClientSummary>
        rowData={data}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        animateRows
        rowSelection="single"
        onRowClicked={(event) => {
          if (event.data) {
            handleRowClick(event.data.clientId);
          }
        }}
        suppressCellFocus
        domLayout="normal"
        rowHeight={56}
        headerHeight={48}
      />
    </Box>
  );
}

export default ClientSummaryTable;
